import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Link } from 'react-router-dom';
import { FaBlog, FaSearch, FaInstagram} from 'react-icons/fa';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';

function Blog() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [allTags, setAllTags] = useState([]);
  const { user } = useAuth();

  // İçeriği formatlamak için yardımcı fonksiyon
  const formatContent = (content) => {
    if (!content) return '';

    // HTML etiketlerini temizle
    let formattedContent = content.replace(/<[^>]+>/g, '');

    // Markdown formatlamasını yap
    formattedContent = formattedContent
      // Başlıkları formatla
      .replace(/^#{1,6}\s+(.+)$/gm, '$1')
      // Madde işaretli listeleri formatla
      .replace(/^\s*[-*+]\s+(.+)$/gm, '• $1')
      // Numaralı listeleri formatla
      .replace(/^\d+\.\s+(.+)$/gm, '$1')
      // Kalın metinleri formatla
      .replace(/\*\*(.+?)\*\*/g, '$1')
      // İtalik metinleri formatla
      .replace(/\*(.+?)\*/g, '$1')
      // Kod bloklarını formatla
      .replace(/`(.+?)`/g, '$1')
      // Fazladan boşlukları temizle
      .replace(/\s+/g, ' ')
      .trim();

    return formattedContent;
  };

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const q = query(collection(db, 'blogs'), orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(q);
        const blogsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          summary: formatContent(doc.data().summary) // İçeriği formatla
        }));
        setBlogs(blogsData);

        // Tüm etiketleri topla
        const tags = new Set();
        blogsData.forEach(blog => {
          if (Array.isArray(blog.tags)) {
            blog.tags.forEach(tag => tags.add(tag));
          }
        });
        setAllTags(Array.from(tags));
      } catch (err) {
        console.error('Blog yazıları yüklenirken bir hata oluştu:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  const filteredBlogs = blogs.filter(blog => {
    const matchesSearch = blog.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         blog.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTag = !selectedTag || (Array.isArray(blog.tags) && blog.tags.includes(selectedTag));
    return matchesSearch && matchesTag;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      {/* Hero Section - Glass Effect */}
      <div className="relative bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-24">
        <div className="absolute inset-0 opacity-10" style={{ 
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cpath d='M0 0h60v60H0z'/%3E%3Cpath d='M30 30h30v30H30z' fill='currentColor' opacity='.05'/%3E%3Cpath d='M0 0h30v30H0z' fill='currentColor' opacity='.05'/%3E%3C/g%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat'
        }}></div>
        <div className="absolute inset-0 bg-gradient-to-t from-blue-600/20 to-transparent"></div>
        <div className="container mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-100">
              Excel Blog
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Excel ve VBA ile ilgili en güncel yazılar ve ipuçları
            </p>
            <div className="flex justify-center gap-4">
              <a
                href="https://www.instagram.com/excelvbamakro/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 bg-white/10 backdrop-blur-sm text-white rounded-xl hover:bg-white/20 transition-all duration-300 border border-white/20"
              >
                <FaInstagram className="mr-2 text-xl" />
                Instagram'da Takip Et
              </a>
              {user && (user.role === 'admin' || user.role === 'editor') && (
                <Link
                  to="/admin/blogs/new"
                  className="inline-flex items-center px-6 py-3 bg-white text-blue-600 rounded-xl hover:bg-blue-50 transition-all duration-300"
                >
                  <span className="mr-2">+</span>
                  Yeni Blog Yazısı
                </Link>
              )}
            </div>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-gray-50 via-gray-50/80 to-transparent"></div>
      </div>

      {/* Search and Filter Section - Floating Card */}
      <div className="container mx-auto px-4 -mt-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg p-6 border border-gray-100"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Blog yazısı ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white/50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
              />
            </div>
            <div>
              <select
                value={selectedTag}
                onChange={(e) => setSelectedTag(e.target.value)}
                className="w-full px-4 py-3 bg-white/50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
              >
                <option value="">Tüm Etiketler</option>
                {allTags.map((tag, index) => (
                  <option key={`tag-${tag}-${index}`} value={tag}>{tag}</option>
                ))}
              </select>
            </div>
          </div>
        </motion.div>

        {/* Blog Cards - Magazine Style */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredBlogs.map((blog, index) => (
            <motion.div
              key={blog.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="group bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
            >
              <div className="relative overflow-hidden">
                {blog.coverImage ? (
                  <img
                    src={blog.coverImage}
                    alt={blog.title}
                    className="w-full h-48 object-cover transform group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-48 bg-gradient-to-br from-blue-400 via-indigo-500 to-purple-500 flex items-center justify-center transform group-hover:scale-105 transition-transform duration-500">
                    <FaBlog className="text-white/30 text-5xl" />
                  </div>
                )}
                {Array.isArray(blog.tags) && blog.tags.length > 0 && (
                  <div className="absolute top-4 right-4 flex flex-wrap gap-2 max-w-[70%] justify-end">
                    {blog.tags.map((tag, tagIndex) => (
                      <span
                        key={tagIndex}
                        className="px-2 py-1 bg-white/90 backdrop-blur-sm text-blue-600 rounded-lg text-xs font-medium shadow-sm"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black/70 to-transparent"></div>
                <div className="absolute bottom-4 left-4 text-white">
                  <p className="text-xs font-medium opacity-90">
                    {new Date(blog.createdAt?.toDate()).toLocaleDateString('tr-TR')}
                  </p>
                </div>
              </div>
              <div className="p-4">
                <h2 className="text-lg font-bold mb-2 text-gray-800 group-hover:text-blue-600 transition-colors duration-300 line-clamp-2">
                  {blog.title}
                </h2>
                <p className="text-gray-600 mb-4 line-clamp-3 text-xs leading-relaxed">
                  {blog.summary}
                </p>
                <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center transform rotate-3 group-hover:rotate-6 transition-transform duration-300">
                      <span className="text-white text-xs font-bold">
                        {blog.author?.charAt(0)?.toUpperCase() || 'A'}
                      </span>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-900">{blog.authorRole}</p>
                      <p className="text-xs text-gray-500">Rol</p>
                    </div>
                  </div>
                  <Link
                    to={`/blog/${blog.id}`}
                    className="inline-flex items-center space-x-1 text-blue-600 hover:text-blue-800 font-medium text-xs group"
                  >
                    <span className="relative">
                      <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-600 group-hover:w-full transition-all duration-300"></span>
                      Devamını Oku
                    </span>
                    <svg 
                      className="w-3 h-3 transform group-hover:translate-x-1 transition-transform duration-300" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {filteredBlogs.length === 0 && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <div className="w-24 h-24 mx-auto mb-6 rounded-3xl bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center transform -rotate-6">
              <FaBlog className="text-blue-300 text-4xl" />
            </div>
            <p className="text-2xl font-semibold text-gray-600 mb-2">
              {searchTerm || selectedTag ? 'Aradığınız İçerik Bulunamadı' : 'Henüz Blog Yazısı Yok'}
            </p>
            <p className="text-gray-500">
              {searchTerm || selectedTag
                ? 'Farklı anahtar kelimeler deneyebilir veya tüm yazıları görüntüleyebilirsiniz.'
                : 'Çok yakında yeni yazılar eklenecek.'}
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
}

export default Blog; 